import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import { baseFastApiMatterRoute } from "@/lib/data/requests/matters/matter";
import {
  type APIResponse,
  type MatterInsurance,
  type MatterInsuranceData,
  type SourceOwnedConnectionCreate,
} from "@/lib/types";

const matterInsurancePayloadKeys = [
  "category",
  "claimNumber",
  "coverage",
  "coverageEnabled",
  "dateModified",
  "groupNumber",
  "id",
  "liability",
  "liabilityPercentage",
  "matterId",
  "medPay",
  "medPayLimit",
  "memberNumber",
  "policyLimitConfirmation",
  "policyLimitPerIncident",
  "policyLimitPerPerson",
  "policyNumber",
  "subrogation",
  "subrogationFileNumber",
  "type",
  "uimExists",
  "uimPerIncident",
  "uimPerPerson",
  "umExists",
  "umPerIncident",
  "umPerPerson",
  "umbrellaPolicyLimit",
] as const;

const getMatterInsurancePayload = (
  data: Partial<MatterInsurance>,
): Partial<MatterInsurance> =>
  Object.fromEntries(
    matterInsurancePayloadKeys
      .filter((key) => key in data)
      .map((key) => [key, data[key]]),
  ) as Partial<MatterInsurance>;

export type CreateMatterInsuranceV3Input = Pick<
  MatterInsuranceData,
  "matterId" | "type"
> &
  Partial<MatterInsuranceData> & {
    connections?: SourceOwnedConnectionCreate[];
  };

export type InsuranceRelatedMatterPlacement = {
  matterId: string;
  placement: "client" | "other_party";
};

export type CopyPartyInsuranceToRelatedMattersInput = {
  insuranceId: string;
  placements: InsuranceRelatedMatterPlacement[];
  sourceMatterContactConnectionId: string;
  sourceMatterId: string;
};

export const createMatterInsuranceV3 = (data: CreateMatterInsuranceV3Input) => {
  const { connections = [], ...insuranceData } = data;
  return authenticatedFastApiAxiosInstance
    .post<APIResponse<MatterInsuranceData>>("matters/v4/insurances", {
      ...getMatterInsurancePayload(insuranceData),
      connections,
    })
    .then((res) => res.data);
};

export const copyPartyInsuranceToRelatedMatters = ({
  insuranceId,
  ...data
}: CopyPartyInsuranceToRelatedMattersInput) =>
  authenticatedFastApiAxiosInstance
    .post<
      APIResponse<MatterInsuranceData>
    >(`matters/v4/insurances/${encodeURIComponent(insuranceId)}/link`, data)
    .then((res) => res.data);

export const updateMatterInsuranceV1 = (
  data: Pick<MatterInsuranceData, "id" | "dateModified"> &
    Partial<MatterInsuranceData>,
) => {
  return authenticatedFastApiAxiosInstance
    .patch<
      APIResponse<MatterInsuranceData>
    >(`${baseFastApiMatterRoute}/insurances`, getMatterInsurancePayload(data))
    .then((res) => res.data);
};

export const deleteMatterInsuranceV1 = (
  data: Pick<MatterInsuranceData, "id" | "matterId">,
) => {
  const { id } = data;
  return authenticatedFastApiAxiosInstance
    .delete<
      APIResponse<MatterInsuranceData>
    >(`${baseFastApiMatterRoute}/insurances/${id}`)
    .then((res) => res.data);
};
